<template>
    <div>
        <Carousel
            v-if="banners.length"
            class="relative w-full"
            :opts="{
                align: 'center',
                direction: 'rtl',
                loop: true,
            }"
            :plugins="[
                Autoplay({
                    delay: 8000,
                }),
            ]"
            @init-api="setApi"
        >
            <CarouselContent>
                <CarouselItem v-for="(item, index) in banners" :key="index">
                    <!-- Slide wrapper: foreground content scrolls normally; background parallax behind -->
                    <div :ref="(el) => (slideEls[index] = el as HTMLElement)" class="relative w-full h-screen overflow-hidden">
                        <!-- Background layer with parallax -->
                        <div
                            class="z-0 absolute inset-0 bg-cover bg-no-repeat bg-center will-change-transform"
                            :style="{
                                ...getGeneratedImage(item.media.image),
                                transform: `translateY(${(parallax[index] ?? 0) * 3}px)`,
                            }"
                        />

                        <!-- Foreground content sits above background and scrolls with page -->
                        <div v-if="item.name || item.description" class="bottom-0 left-0 z-10 absolute opacity-0 slide-content">
                            <Glass rounded="none" padding="lg" blur="sm" class="pb-28 pl-28 rounded-tr-xl">
                                <div class="relative mt-4 w-4/5 max-w-sm">
                                    <template v-if="item.url">
                                        <NuxtLink :to="item.url" class="flex gap-4">
                                            <Typography tag="h3">{{ item.name }}</Typography>
                                            <Icon name="majesticons:open" class="size-6! text-primary" />
                                        </NuxtLink>
                                    </template>
                                    <template v-else>
                                        <Typography tag="h3">{{ item.name }}</Typography>
                                    </template>
                                    <Typography variant="body" class="mt-4 font-normal line-clamp-3">
                                        {{ item.description }}
                                    </Typography>
                                </div>
                            </Glass>
                        </div>
                    </div>
                </CarouselItem>
            </CarouselContent>
        </Carousel>
    </div>
</template>

<script lang="ts" setup>
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import type { CarouselApi } from "@/components/ui/carousel";
import { Glass } from "@/components/ui/glass";
import { utils } from "animejs";
import Autoplay from "embla-carousel-autoplay";

interface Props {
    banners: APIBanner[];
}

defineProps<Props>();
const emit = defineEmits(["update:index", "update:slide"]);
const current = defineModel<number>();

const api = ref<CarouselApi>();
const { anime } = useAnimate();
const img = useImage();

const totalCount = ref(0);
// Per-slide parallax amounts keyed by slide index
const parallax = ref<Record<number, number>>({});
const slideEls = ref<HTMLElement[]>([]);
const stops: Array<() => void> = [];
const animation = ref();

onMounted(async () => {
    slideEls.value.forEach((el, i) => {
        if (!el) return;
        const bounds = useElementBounding(el);
        const stop = watchEffect(() => {
            // Parallax based on element's viewport top; adjust multiplier to taste
            parallax.value[i] = -bounds.top.value * 0.2;
        });
        stops.push(stop);
    });

    const slideContent = utils.$(".slide-content");
    animation.value = await anime(slideContent, {
        delay: 500,
        duration: 500,
        opacity: {
            from: 0,
            to: 1,
        },
        translateY: {
            from: 25,
            to: 0,
        },
    });
    animation.value?.play();
});

function getGeneratedImage(image: APIMediaCollection) {
    const imgString = getSingleImage(image)?.src;
    if (imgString) {
        const imgUrl = img(imgString, { format: "webp", quality: "95", width: "1920" });
        return { backgroundImage: `url('${imgUrl}')` };
    }

    return null;
}

onBeforeUnmount(() => {
    stops.forEach((stop) => stop());
});

function setApi(val: CarouselApi) {
    api.value = val;
}

watchOnce(api, (api) => {
    if (!api) return;

    totalCount.value = api.scrollSnapList().length;
    current.value = api.selectedScrollSnap() + 1;

    api.on("select", () => {
        animation.value.restart();
        current.value = api.selectedScrollSnap() + 1;
        emit("update:index", current.value);
    });
});

const carousel = {
    nextSlide: () => api.value?.scrollNext(),
    prevSlide: () => api.value?.scrollPrev(),
    goToSlide: (index: number, jump?: boolean) => api.value?.scrollTo(index, jump),
};

defineExpose(carousel);
</script>

<style></style>
